import { defineStore, navigateTo } from '#imports';
import { userSignService, userProfileService } from './../api';
import type { AuthTypes, ProfileTypes } from './../types';
import { responseMessages } from '@/app/messages';

import { useUserDialogStore } from './dialog';
import { useDialogResponseStore } from '@/widgets/dialog-response/store';

const enum UserAuthStatus {
	Unknown = 'unknown',
	Auth = 'auth',
	Unauth = 'unauth',
}

interface State {
	authStatus: UserAuthStatus;
	accessToken: string;
	changePasswordToken: string;
	user: ProfileTypes.UserProfileResponse | null;
}

export const useUserAuthStore = defineStore('UserAuth', {
	state: (): State => ({
		authStatus: UserAuthStatus.Unknown,
		accessToken: '',
		changePasswordToken: '',
		user: null,
	}),

	getters: {
		isAuth: ({ authStatus }) => authStatus === UserAuthStatus.Auth,
	},

	actions: {
		resetProfileData() {
			this.authStatus = UserAuthStatus.Unauth;
			this.accessToken = '';
			this.user = null;
		},

		async refresh() {
			const data = await userSignService.refresh();

			if ('error' in data) throw data;

			this.authStatus = data.access?.length
				? UserAuthStatus.Auth
				: UserAuthStatus.Unauth;

			this.accessToken = data.access;
		},

		async login(payload: AuthTypes.LoginPayload) {
			const data = await userSignService.login(payload);

			if ('error' in data) {
				return data;
			}

			this.authStatus = data.access?.length
				? UserAuthStatus.Auth
				: UserAuthStatus.Unauth;

			this.accessToken = data.access;
			await navigateTo('/');

			return data;
		},

		async logout() {
			const { setResponseModalMessage, showResponseModal } = useDialogResponseStore();
			const { closeUserDialog } = useUserDialogStore();

			await userSignService.logout();

			this.resetProfileData();
			setResponseModalMessage(responseMessages.success.logout);
			setTimeout(() => showResponseModal(), 0);
			closeUserDialog();
		},

		async profile() {
			const data = await userProfileService.fetchProfile();

			// TODO: в зависимости от ошибки, мб нужно очищать данные
			if ('error' in data) {
				return data;
			}

			this.user = data;

			return data;
		},

		setChangePasswordToken(token: string) {
			this.changePasswordToken = token;
		},
	},
});
